import { PositionColumn } from './positions-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Table,
} from '@/components/ui/';
import { MoreVertical } from 'lucide-react';
import {
  abbrFromSym,
  imgFromAbbr,
  symbolToBaseQuote,
} from '@/utils/token-symbol';
import { useState } from 'react';
import { TPSLEdit } from './tpsl-edit';
import useGeofenceCheck from '@/utils/geofence-check';
import { bigIntToDecimalStr, formatNumber } from '@/utils/value-format';
import { cn } from '@/lib/utils';
import { useMarketStore } from '@/store/use-markets-store';
import { useStore } from 'zustand';

export default function MobilePositionMenu({
  symbol,
  entryPrice,
  rawPosition,
  isLong,
  leverage,
  size,
  positionSls,
  positionTps,
  markPrice,
  liqPrice,
  unrealizedPnl,
  roePct,
  value,
  fundingPaid,
}: PositionColumn) {
  const [areDetailsOpen, setAreDetailsOpen] = useState(false);
  const { data: isSanctioned } = useGeofenceCheck();
  const { priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec,
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="h-full pl-2">
        <MoreVertical size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <Dialog open={areDetailsOpen} onOpenChange={setAreDetailsOpen}>
          <DialogTrigger asChild>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              Position Details
            </DropdownMenuItem>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="font-mono text-base font-normal uppercase">
                {/* <DialogTitle className="text-left font-mono font-normal uppercase tracking-wider"> */}
                Position Details
              </DialogTitle>
            </DialogHeader>
            <div className="flex gap-4 px-6 pb-8">
              <img
                src={imgFromAbbr(abbrFromSym(symbol))}
                title={symbol}
                className="h-7 w-7"
              />
              <div className="mt-1 grid flex-1 grid-cols-2 items-end gap-4">
                <div className="mb-3 flex h-4 items-center gap-3">
                  <span className="font-sans">{symbolToBaseQuote(symbol)}</span>
                  <div className="rounded bg-[#271714] px-1.5">
                    <span className="font-mono text-sm text-primary">
                      {formatNumber(leverage, {
                        style: 'leverage',
                        maxDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
                <div className="mb-3 flex h-4 items-end gap-3">
                  <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                    Side
                  </span>
                  <span
                    className={cn(
                      'font-mono uppercase leading-none',
                      isLong ? 'text-green' : 'text-red',
                    )}
                  >
                    {isLong ? 'Long' : 'Short'}
                  </span>
                </div>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  Size
                </span>
                <span className="font-mono uppercase leading-none">{`${bigIntToDecimalStr(
                  size,
                  sizeDecimals,
                )} ${abbrFromSym(symbol)}`}</span>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  Value
                </span>
                <span className="font-mono uppercase leading-none">
                  {formatNumber(value.decimal, { digits: 2, round: 'ceil' })}
                </span>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  Entry Price
                </span>
                <span className="font-mono uppercase leading-none">
                  {formatNumber(bigIntToDecimalStr(entryPrice, priceDecimals))}
                </span>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  Mark Price
                </span>
                <span className="font-mono uppercase leading-none">
                  {formatNumber(bigIntToDecimalStr(markPrice, priceDecimals))}
                </span>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  Liq Price
                </span>
                <span className="font-mono uppercase leading-none">
                  {formatNumber(liqPrice.decimal)}
                </span>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  PNL
                </span>
                <span
                  className={cn(
                    'font-mono uppercase leading-none',
                    Number(unrealizedPnl) < 0 ? 'text-red' : 'text-green',
                  )}
                >
                  {formatNumber(unrealizedPnl.decimal, {
                    showChange: true,
                    digits: 2,
                  })}{' '}
                  {`(${formatNumber(roePct, {
                    digits: 2,
                    style: 'percent',
                    abs: true,
                  })})`}
                </span>
                <span className="font-mono text-sm uppercase leading-none tracking-wider text-vestgrey-100">
                  Funding
                </span>
                <span
                  className={cn(
                    'font-mono uppercase leading-none',
                    fundingPaid.bigint < 0n ? 'text-red' : 'text-green',
                  )}
                >
                  {formatNumber(fundingPaid.decimal, {
                    showChange: true,
                    digits: 2,
                  })}
                </span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <TPSLEdit
          symbol={symbol}
          currentPosition={rawPosition}
          currentTps={positionTps}
          currentSls={positionSls}
          disabled={isSanctioned}
        >
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            Edit TP/SL
          </DropdownMenuItem>
        </TPSLEdit>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
